/* eslint-disable import/no-cycle */
import {
  useCallback, useRef, useState,
} from 'react';
import type { MeiDou } from '@meitu/subscribe';
import { getSubscribe } from '$/common/utils/subscribeUtil';
import { useOfficialStore } from '$/store/useOfficialStore';
import { appKey } from '$/common/envMap';
import { getGclid, getGoogleClientId, getUrlSearchParams } from '$/utils/index';
import { getSourceByPath } from '$/utils/getSourceByPath';
import useVipModalStore, { type VipModalTrackParams } from '$/store/useVipModalStore';
import useThemeStore from '$/store/useThemeStore';
import {
  getVipModalTrackParams,
  getVipModalTrackValue,
  getSubscribeOrderBigDataParams,
} from '$/utils/subscribeTrack';
import type { AgentTeamSubscriptionProductsParams } from '$/domains/teamAgent/type';
import { message } from '$/components/Message';
import { notifyRequestNetworkErrorOnce } from '$/api/utils/resolveRequestErrorMessage';

export enum OrderOperatingSystem {
  Unknown,
  Android,
  AndroidPad,
  iOS,
  Windows,
  Linux,
  iPadOS,
  macOS,
  harmonyOS,
}

export interface SubscribeTeamProductListMapResult<TProduct, TExtraState extends Record<string, any>> {
  productList: TProduct[];
  extraState: TExtraState;
}

interface UseSubscribeTeamProductListOptions<TResult, TProduct, TExtraState extends Record<string, any>> {
  trackParams?: VipModalTrackParams;
  requestParams?: AgentTeamSubscriptionProductsParams;
  includeRebateCode?: boolean;
  initialExtraState: TExtraState;
  errorMessage: string;
  fetchProducts: (params: AgentTeamSubscriptionProductsParams) => Promise<TResult>;
  mapResult: (result: TResult) => SubscribeTeamProductListMapResult<TProduct, TExtraState>;
}

/**
 * 获取当前浏览器环境对应的订阅订单操作系统枚举。
 */
export function getOperatingSystem(): OrderOperatingSystem {
  const { userAgent } = navigator;

  const isAndroid = /Android/.test(userAgent);
  const isMobile = /Mobile/.test(userAgent);
  const isIpad = /iPad/.test(userAgent);
  const isIOS = /iPhone|iPad|iPod/.test(userAgent);
  const isWindows = /Windows/.test(userAgent);
  const isLinux = /Linux/.test(userAgent);
  const isMacOS = /Mac OS|Macintosh/.test(userAgent);

  if (isAndroid && isMobile) return OrderOperatingSystem.Android;

  if (isAndroid && isIpad) return OrderOperatingSystem.AndroidPad;

  if (isIpad) return OrderOperatingSystem.iPadOS;

  if (isIOS) return OrderOperatingSystem.iOS;

  if (isWindows) return OrderOperatingSystem.Windows;

  if (isLinux) return OrderOperatingSystem.Linux;

  if (isMacOS) return OrderOperatingSystem.macOS;

  return OrderOperatingSystem.Unknown;
}

/**
 * 将接口返回的 snake_case 或 kebab-case key 转成订阅 SDK 商品消费的 camelCase key。
 */
export function toCamelCase(str: string): string {
  return str.replace(/([-_][a-z])/gi, ($1) => {
    return $1.toUpperCase().replace(/[-_]/g, '');
  });
}

/**
 * 递归转换接口返回字段，保证商品展示和支付链路继续读取 camelCase 字段。
 */
export function deepKeysToCamelCase<T>(obj: T): T {
  if (Array.isArray(obj)) {
    return obj.map((item) => deepKeysToCamelCase(item)) as T;
  }

  if (obj && typeof obj === 'object' && !(obj instanceof Date)) {
    const result: any = {};
    for (const [key, value] of Object.entries(obj as object)) {
      const camelKey = toCamelCase(key);
      result[camelKey] = deepKeysToCamelCase(value);
    }
    return result as T;
  }

  return obj;
}

/**
 * 合并 Team 商品接口需要的客户端上下文参数和业务额外参数。
 */
function getTeamProductRequestParams(
  baseParams: AgentTeamSubscriptionProductsParams,
  requestParams?: AgentTeamSubscriptionProductsParams,
): AgentTeamSubscriptionProductsParams {
  return {
    ...baseParams,
    ...(requestParams || {}),
  };
}

/**
 * 生成订阅 SDK 的订单透传基础数据，VIP 订阅链路需要额外携带返利码。
 */
function getSubscribeBaseData(includeRebateCode?: boolean) {
  const baseData: Record<string, any> = {
    device_type: 3,
    oper_system: getOperatingSystem(),
  };

  if (includeRebateCode) {
    baseData.open_params = {
      rebate_info: {
        rebate_code: useVipModalStore.getState().rebateCode,
      },
    };
  }

  return baseData;
}

/**
 * 统一管理 Team 商品列表拉取前的订阅 SDK 透传、公共参数和基础状态。
 */
function useSubscribeTeamProductList<TResult, TProduct, TExtraState extends Record<string, any>>(
  options: UseSubscribeTeamProductListOptions<TResult, TProduct, TExtraState>,
) {
  const {
    trackParams,
    requestParams,
    includeRebateCode,
    initialExtraState,
    errorMessage,
    fetchProducts,
    mapResult,
  } = options;
  const [productList, setProductList] = useState<TProduct[]>([]);
  const [extraState, setExtraState] = useState<TExtraState>(initialExtraState);
  const [transferId, setTransferId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const currentCountryCode = useOfficialStore((state) => state.currentCountryCode);
  const firstUrl = useOfficialStore((state) => state.firstUrl);
  const channel = useOfficialStore((state) => state.channel);
  const gid = useOfficialStore((state) => state.gid);
  const env = useOfficialStore((state) => state.env);
  const userAccountKey = useOfficialStore((state) => `${state.userInfo.uid || ''}:${state.userInfo.token || ''}`);
  const sentrySdkVersion = useOfficialStore((state) => state.sentryConfig?.SDK_VERSION);
  const theme = useThemeStore((state) => state.theme);
  const trackParamsRef = useRef(trackParams);
  const latestRequestIdRef = useRef(0);
  trackParamsRef.current = trackParams;

  /**
   * 初始化订阅 SDK 生成 transferId，再按业务配置拉取 Team 商品列表。
   */
  const initPaySdk = useCallback(async () => {
    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;
    setLoading(true);
    setError(null);
    setProductList([]);
    setTransferId('');
    setExtraState(initialExtraState);

    try {
      const instance = await getSubscribe() as MeiDou;
      const gg_client_id = await getGoogleClientId();
      const gclid = await getGclid();
      const modalTrackParams = getVipModalTrackParams(trackParamsRef.current);
      const fromSource = getVipModalTrackValue(
        modalTrackParams,
        'from_source',
        getUrlSearchParams().get('from_source') || useVipModalStore.getState().openMode,
      );
      const { transferId: trackedTransferId } = await instance.trackSubscribeData({
        /**
         * 参阅透传字段约定：https://cf.meitu.com/confluence/pages/viewpage.action?pageId=253007602
         * 其中：device_type（终端来源）、oper_system（操作系统）与最终订单落库相关。
         */
        baseData: JSON.stringify(getSubscribeBaseData(includeRebateCode)),
        bigData: JSON.stringify({
          device_id: gid,
          app_key: appKey[env],
          order_type: 1,
          country_code: currentCountryCode,
          channel,
          gg_client_id,
          gclid,
          position_type: getVipModalTrackValue(modalTrackParams, 'position_type', getSourceByPath()),
          from_source: fromSource,
          ...getSubscribeOrderBigDataParams(modalTrackParams),
          // NOTICE: 订阅sdk跟中台通过接口透传数据时没有encode的字段会直接报错，先保持业务侧 encode。
          first_url: encodeURIComponent(firstUrl),
        }),
        /**
         * 业务期望透传至收银台埋点的额外公共参数。
         */
        businessData: '',
      });

      const currentUserInfo = useOfficialStore.getState().userInfo;
      const currentAccountKey = `${currentUserInfo.uid || ''}:${currentUserInfo.token || ''}`;
      if (latestRequestIdRef.current !== requestId || currentAccountKey !== userAccountKey) return;
      setTransferId(trackedTransferId);

      const productInfo = await fetchProducts(getTeamProductRequestParams({
        version: sentrySdkVersion || '1.0.0',
        theme: theme === 'light' ? 1 : 2,
        client_os: String(getOperatingSystem()),
        client_channel_id: channel,
        // area_code: 'US',
        // lang:'en'
      }, requestParams));
      const mappedResult = mapResult(productInfo);
      const latestUserInfo = useOfficialStore.getState().userInfo;
      const latestAccountKey = `${latestUserInfo.uid || ''}:${latestUserInfo.token || ''}`;
      if (latestRequestIdRef.current !== requestId || latestAccountKey !== userAccountKey) return;
      setProductList(mappedResult.productList);
      setExtraState(mappedResult.extraState);
    } catch (err) {
      if (latestRequestIdRef.current !== requestId) return;
      setError(err as Error);
      // 订阅 SDK 使用原生 Fetch，请在业务层拦截断网错误，禁止向用户暴露请求域名和接口细节。
      notifyRequestNetworkErrorOnce(err, (key) => message.error(key));
      console.error(errorMessage, err);
    } finally {
      if (latestRequestIdRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [
    channel,
    currentCountryCode,
    env,
    errorMessage,
    fetchProducts,
    firstUrl,
    gid,
    includeRebateCode,
    initialExtraState,
    mapResult,
    requestParams,
    sentrySdkVersion,
    theme,
    userAccountKey,
  ]);

  /**
   * 暴露刷新方法，保持业务 hook 调用形态稳定。
   */
  const refetch = useCallback(() => {
    return initPaySdk();
  }, [initPaySdk]);

  return {
    transferId,
    productList,
    extraState,
    refetch,
    loading,
    error,
  };
}

export default useSubscribeTeamProductList;
